/* eslint-disable react-hooks/exhaustive-deps */
import React, { memo, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
// libraries
import { Box, Stack, Button, Tooltip } from '@mui/material';
// Components
import { addScriptRowData, getCallScriptSection } from 'src/redux/slices/callScript';
import { getPracticeTreatmentList } from 'src/redux/slices/practiceTreatment';
import { getPracticeItem } from 'src/redux/slices/practice';
import { shallowEqual } from 'react-redux';
import useFormsFlags from 'src/hooks/useFormsFlags';
import NavList from './NavList';
import Iconify from '../../Iconify';
import { ConsentBuilderPopUp, ReviewLinkPopUp } from '../../dialogPopUp';

import SendFormsDialog from '../../dialogPopUp/SendFormsDialog';
import { OpenCallScriptPopup } from '../../fullScreenPopUps';
import CustomMenuItem from '../CustomMenuItem';
// Redux
import { clearConsentData } from '../../../redux/slices/lead';
import { openCreateLeadDialog, openDentallyBookingDialog } from '../../../redux/slices/createLeadDialog';
import { dispatch, useSelector } from '../../../redux/store';
// Hooks/routes/constants
import { useAuth, useToggle, useConsentToggle } from '../../../hooks';
import { PATH_DASHBOARD } from '../../../routes/paths';
import { allowedTitlesMini } from '../../../constants/commonConstants';
import { localStorageRemoveItem } from '../../../utils/storage-available';
import { isNavItemHidden, titleToSlug } from '../hiddenNavItems';
// style
import { createButton } from './styles';

const NavSectionMini = ({ data, sx, isPractitioner, ...other }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const navigate = useNavigate();

    const handleClick = (event) => setAnchorEl(event.currentTarget);

    const createHandleClose = () => setAnchorEl(null);

    const {
        toggle: openCallScript,
        onOpen: onOpencallScript,
        onClose: onCloseCallScript
    } = useToggle();

    const {
        toggle: ConsentBuilder,
        onOpen: onOpenConsent,
        onClose: onCloseConsent
    } = useConsentToggle();

    const {
        toggle: openReview,
        onOpen: onOpenReview,
        onClose: onCloseReview
    } = useToggle();

    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    const { dentallyEnabledGlobal } = useSelector((state) => state.practice);
    const { practiceUser: { results: practiceUsers } } = useSelector(state => state.practiceUser);
    const { user } = useAuth();
    const isSuperAdmin = practiceUsers?.find(el => el.user?.id === user?.pk)?.user?.is_superuser;

    // Consent form flags — read from feature flags (replaces the old choicesData derivation)
    const { v1Enabled, v2Enabled } = useFormsFlags();
    const [consentV2Open, setConsentV2Open] = useState(false);

    const onCloseCallScriptPopup = () => {
        const commonParams = {
            practiceId,
            limit: 10,
            offset: 0,
        };
        [false, true].forEach((isTemplate) => {
            dispatch(getCallScriptSection({ ...commonParams, isTemplate }));
        });
        onCloseCallScript();
    };

    const handleReviewClick = () => { onOpenReview(); createHandleClose(); };
    const handleClickNewLead = () => {
        localStorageRemoveItem('localTreatmentData');
        dispatch(openCreateLeadDialog());
        createHandleClose();
    };
    const handleClickBookDentally = () => {
        dispatch(openDentallyBookingDialog());
        createHandleClose();
    };
    const handleClickConsentV1Click = () => {
        onOpenConsent();
        dispatch(clearConsentData());
        createHandleClose();
    };
    const handleClickConsentV2Click = () => {
        setConsentV2Open(true);
        createHandleClose();
    };
    const handleClickStripePaymentClick = () => { navigate(PATH_DASHBOARD.createStripePayment.root); createHandleClose(); };
    const handleClickNewCampaign = () => { navigate(PATH_DASHBOARD.campaign.campaign); createHandleClose(); };
    const handleClickBookingWhatsApp = () => { navigate(PATH_DASHBOARD.whatsapp.root(practiceId)); createHandleClose(); };
    const handleClickCallScriptClick = () => {
        dispatch(getCallScriptSection({ practiceId, limit: 10, offset: 0, isTemplate: false }));
        dispatch(getCallScriptSection({ practiceId, limit: 10, offset: 0, isTemplate: true }));
        dispatch(addScriptRowData(''));
        dispatch(getPracticeTreatmentList(practiceId));
        dispatch(getPracticeItem(practiceId));
        onOpencallScript();
        // navigate(PATH_DASHBOARD.user.callScripts, { state: { callScriptPopupOpen: true } });
        createHandleClose();
    };

    return (
        <Stack
            spacing={0.5}
            alignItems='center'
            sx={{
                px: 0.75,
                pb: '24px',
                ...sx,
            }}
            {...other}
        >
            <Stack sx={{ margin: '30px 0 8px', width: '100%', alignItems: 'center' }}>
                {isPractitioner ? null : <Tooltip title='Create'>
                    <Button
                        sx={createButton}
                        variant='contained'
                        onClick={handleClick}
                    >
                        <Iconify icon={'eva:plus-fill'} />
                    </Button>
                </Tooltip>}
                <CustomMenuItem
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={createHandleClose}
                    handleClickCallScriptClick={handleClickCallScriptClick}
                    handleClickConsentV1Click={handleClickConsentV1Click}
                    handleClickConsentV2Click={handleClickConsentV2Click}
                    handleClickNewLead={handleClickNewLead}
                    handleClickStripePaymentClick={handleClickStripePaymentClick}
                    handleReviewClick={handleReviewClick}
                    handleClickNewCampaign={handleClickNewCampaign}
                    handleClickBookingWhatsApp={handleClickBookingWhatsApp}
                    dentallyEnabled={dentallyEnabledGlobal && !!isSuperAdmin}
                    handleClickBookDentally={handleClickBookDentally}
                    v1Enabled={v1Enabled}
                    v2Enabled={v2Enabled}
                />
            </Stack>
            {data.map((group, index) => (
                <Items key={group.subheader} items={group.items} isLastGroup={index + 1 === data.length} />
            ))}
            {ConsentBuilder && v1Enabled && <ConsentBuilderPopUp
                open={ConsentBuilder}
                onClose={onCloseConsent}
            />}
            {consentV2Open && <SendFormsDialog
                open={consentV2Open}
                onClose={() => setConsentV2Open(false)}
                practiceId={practiceId}
            />}
            {openReview && <ReviewLinkPopUp
                open={openReview}
                onClose={onCloseReview}
            />}
            {openCallScript && <OpenCallScriptPopup
                open={openCallScript}
                headerButtons
                onClose={() => onCloseCallScriptPopup()}
                type={'callScript'}
            />}
        </Stack>
    );
};

export default memo(NavSectionMini);

const Items = ({ items, isLastGroup }) => {
    const { practiceUser: { results } } = useSelector(state => state.practiceUser);
    const { dentallyEnabledGlobal } = useSelector((state) => state.practice);
    const practice = useSelector((state) => state.practice?.practice);
    const permissions = useSelector((state) => state.permissions?.permissions, shallowEqual);
    const choicesData = useSelector((state) => state.practiceTreatment?.choicesData?.results || []);
    const hiddenNavItems = practice?.preferences?.hidden_nav_items || [];

    const { user } = useAuth();

    const getUserList = () => results && results?.length > 0 && results?.filter(el => el.user && el.user.id === user?.pk);

    return (
        <>
            {items.map((list) => {
                const normalisedTitle = list?.title?.toLowerCase?.() || '';
                const isSuperAdminItem = normalisedTitle === 'super admin';
                const canRenderSuperAdmin = isSuperAdminItem && getUserList()[0]?.user?.is_superuser;
                const isAllowed = allowedTitlesMini.includes(normalisedTitle) || canRenderSuperAdmin;

                if (!isAllowed) {
                    return '';
                }

                // Feature permission check.
                // - Default (fail-open): hide only when permissions are loaded AND the
                //   feature is explicitly 'hidden'.
                // - `requirePermission: true` (fail-closed): hide until the feature is
                //   explicitly granted ('view' or 'manage').
                if (list.feature) {
                    const featurePermission = permissions?.[list.feature];
                    if (list.requirePermission) {
                        if (featurePermission !== 'view' && featurePermission !== 'manage') {
                            return '';
                        }
                    } else if (permissions && Object.keys(permissions).length > 0) {
                        if (featurePermission === 'hidden') {
                            return '';
                        }
                    }
                }

                // Website annotation always visible — sessions stored in our backend

                if (normalisedTitle === 'dentally') {
                    if (!dentallyEnabledGlobal) {
                        return '';
                    }
                }

                // Filter children by feature permissions — same fail-open / fail-closed
                // distinction as the parent above.
                const featureFilteredChildren = list.children
                    ? list.children.filter((child) => {
                        if (!child.feature) return true;
                        const childPerm = permissions?.[child.feature];
                        if (child.requirePermission) {
                            return childPerm === 'view' || childPerm === 'manage';
                        }
                        if (permissions && Object.keys(permissions).length > 0) {
                            return childPerm !== 'hidden';
                        }
                        return true;
                    })
                    : list.children;

                // Practice nav preferences — cosmetic hide for sub-views
                const navPrefFilteredChildren = featureFilteredChildren?.filter((child) => {
                    if (child.divider) return true;
                    return !isNavItemHidden(titleToSlug(child?.title), hiddenNavItems);
                });

                // Filter Marketing Brief child by enableClickup
                const clickupFilteredChildren = navPrefFilteredChildren?.filter((child) => {
                    if (child?.title?.toLowerCase() === 'marketing brief') {
                        const clickupCfg = (choicesData || []).find(
                            (c) => String(c?.name || '').trim().toLowerCase() === 'clickup config'
                        );
                        const enableCu = String(clickupCfg?.metadata?.enableClickup ?? '').trim().toLowerCase();
                        return enableCu === 'yes' || enableCu === 'true' || enableCu === '1';
                    }
                    return true;
                });

                const filteredList = list.children
                    ? { ...list, children: clickupFilteredChildren || [] }
                    : list;

                return (
                    <NavList
                        key={list.title + list.path}
                        data={filteredList}
                        depth={1}
                        hasChild={!!list.children}
                    />
                );
            })}

            {!isLastGroup && (
                <Box
                    sx={{
                        width: 24,
                        height: '1px',
                        bgcolor: 'divider',
                        my: '8px !important',
                    }}
                />
            )}
        </>
    );
};
NavSectionMini.propTypes = {
    sx: PropTypes.object,
    data: PropTypes.array,
    isPractitioner: PropTypes.bool
};

Items.propTypes = {
    items: PropTypes.array,
    isLastGroup: PropTypes.bool,
};
